import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  Alert,
  Button,
  DatePicker,
  Empty,
  Input,
  Popover,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ApiOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MenuOutlined,
  ReloadOutlined,
  SettingOutlined,
  WifiOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { selectors } from 'farmx-redux-core';
import { useSelector } from 'react-redux';
import Page404 from 'components/app/Page404';
import { getEntityMonitoringData, useGetMaxWidth } from 'helper/common';
import { isMobile } from 'utils/detectDevice';
import EditableTable from './EditableTable';
import EntityMonitoringDrawer from './EntityMonitoringDrawer';
import {
  buildBlockRows,
  buildEntityDrawerData,
  buildEntityRows,
  buildGatewayRows,
  buildRanchRows,
  buildSensorRows,
  formatLabel,
  getGatewayDisplayValue,
  getDeviceStatsForRow,
  getPercent,
  MONITORING_HEALTH,
  moveColumn,
  normalizeColumnOrder,
  RECOMMENDATION_HEALTH,
  SENSOR_HEALTH_LABELS,
  SENSOR_STATUS,
} from './EntityMonitoring.helpers';
import type {
  MonitoringBlockRow,
  MonitoringEntityRow,
  MonitoringHealth,
  MonitoringRanchRow,
  MonitoringRow,
} from './EntityMonitoring.helpers';
import './ruleSettingsPage.less';

const { selectLoginUserInfo } = selectors;

const SELECTION_KEY = {
  ENTITY: 'entity',
  SENSORS: 'sensors',
  RANCHES: 'ranches',
  BLOCKS: 'blocks',
  ONLINE: 'online',
  DEGRADED: 'degraded',
  OFFLINE: 'offline',
  GATEWAYS: 'gateways',
  GATEWAY_ONLINE: 'gateway_online',
  GATEWAY_OFFLINE: 'gateway_offline',
  RECOMMENDATION_CURRENT: 'recommendation_current',
  RECOMMENDATION_STALE: 'recommendation_stale',
  RECOMMENDATION_MISSING: 'recommendation_missing',
} as const;

type SelectionKey = typeof SELECTION_KEY[keyof typeof SELECTION_KEY];
type ColumnGroup = typeof SELECTION_KEY.ENTITY
  | typeof SELECTION_KEY.SENSORS
  | typeof SELECTION_KEY.RANCHES
  | typeof SELECTION_KEY.BLOCKS
  | typeof SELECTION_KEY.GATEWAYS;
type ColumnOrderState = Partial<Record<ColumnGroup, string[]>>;
type RegionFilter = 'all' | 'INDIA' | 'USA' | 'JAPAN';

interface DetailConfig {
  label: string;
  rows: Record<string, any>[];
  columns: ColumnsType<any>;
}

interface FilterOption {
  key: SelectionKey;
  label: string;
  count?: number;
  tone?: 'default' | 'online' | 'degraded' | 'offline';
}

const DEFAULT_SELECTION: SelectionKey = SELECTION_KEY.ENTITY;
const DEFAULT_SNAPSHOT_DATE = moment().subtract(1, 'day');
const DEFAULT_REGION: RegionFilter = 'all';
const LOADING_INDICATOR = <LoadingOutlined style={{ fontSize: 26 }} spin />;
const COLUMN_ORDER_STORAGE_KEY = 'entity-monitoring-column-order';
const REGION_OPTIONS: Array<{ label: string; value: RegionFilter }> = [
  { label: 'All regions', value: 'all' },
  { label: 'India', value: 'INDIA' },
  { label: 'USA', value: 'USA' },
  { label: 'Japan', value: 'JAPAN' },
];

const SENSOR_SELECTIONS: SelectionKey[] = [
  SELECTION_KEY.SENSORS,
  SELECTION_KEY.ONLINE,
  SELECTION_KEY.DEGRADED,
  SELECTION_KEY.OFFLINE,
];
const GATEWAY_SELECTIONS: SelectionKey[] = [
  SELECTION_KEY.GATEWAYS,
  SELECTION_KEY.GATEWAY_ONLINE,
  SELECTION_KEY.GATEWAY_OFFLINE,
];
const RECOMMENDATION_SELECTIONS: SelectionKey[] = [
  SELECTION_KEY.BLOCKS,
  SELECTION_KEY.RECOMMENDATION_CURRENT,
  SELECTION_KEY.RECOMMENDATION_STALE,
  SELECTION_KEY.RECOMMENDATION_MISSING,
];

const COLUMN_GROUP_BY_SELECTION: Record<SelectionKey, ColumnGroup> = {
  [SELECTION_KEY.ENTITY]: SELECTION_KEY.ENTITY,
  [SELECTION_KEY.SENSORS]: SELECTION_KEY.SENSORS,
  [SELECTION_KEY.RANCHES]: SELECTION_KEY.RANCHES,
  [SELECTION_KEY.BLOCKS]: SELECTION_KEY.BLOCKS,
  [SELECTION_KEY.ONLINE]: SELECTION_KEY.SENSORS,
  [SELECTION_KEY.DEGRADED]: SELECTION_KEY.SENSORS,
  [SELECTION_KEY.OFFLINE]: SELECTION_KEY.SENSORS,
  [SELECTION_KEY.GATEWAYS]: SELECTION_KEY.GATEWAYS,
  [SELECTION_KEY.GATEWAY_ONLINE]: SELECTION_KEY.GATEWAYS,
  [SELECTION_KEY.GATEWAY_OFFLINE]: SELECTION_KEY.GATEWAYS,
  [SELECTION_KEY.RECOMMENDATION_CURRENT]: SELECTION_KEY.BLOCKS,
  [SELECTION_KEY.RECOMMENDATION_STALE]: SELECTION_KEY.BLOCKS,
  [SELECTION_KEY.RECOMMENDATION_MISSING]: SELECTION_KEY.BLOCKS,
};

const COLUMN_LABELS: Record<string, string> = {
  sensorHealth: 'Sensor health',
  recommendationHealth: 'Recommendation status',
  health: 'Overall status',
  context: 'Ranch / Entity',
};

const readColumnOrders = (): ColumnOrderState => {
  try {
    const savedOrders = window.localStorage.getItem(COLUMN_ORDER_STORAGE_KEY);
    const parsedOrders = savedOrders ? JSON.parse(savedOrders) : {};
    return parsedOrders && typeof parsedOrders === 'object' && !Array.isArray(parsedOrders)
      ? parsedOrders
      : {};
  } catch (error) {
    return {};
  }
};

const getColumnKey = (column: ColumnsType<any>[number], index: number) => String(
  column.key || column.dataIndex || index,
);

const STATUS_TAG_COLORS: Record<string, string> = {
  [SENSOR_STATUS.ONLINE]: 'green',
  [SENSOR_STATUS.DEGRADED]: 'orange',
  [SENSOR_STATUS.OFFLINE]: 'red',
  [RECOMMENDATION_HEALTH.CURRENT]: 'green',
  [RECOMMENDATION_HEALTH.STALE]: 'red',
  [RECOMMENDATION_HEALTH.MISSING]: 'orange',
  [RECOMMENDATION_HEALTH.DISABLED]: 'default',
  [MONITORING_HEALTH.ALL_GOOD]: 'green',
  [MONITORING_HEALTH.NEEDS_ATTENTION]: 'orange',
  [MONITORING_HEALTH.CRITICAL]: 'red',
};

const STATUS_TAG_LABELS: Record<string, string> = {
  [RECOMMENDATION_HEALTH.CURRENT]: 'Generated recently',
  [RECOMMENDATION_HEALTH.STALE]: 'Overdue',
  [RECOMMENDATION_HEALTH.MISSING]: 'Not generated',
  [RECOMMENDATION_HEALTH.DISABLED]: 'Not enabled',
  [MONITORING_HEALTH.ALL_GOOD]: 'Healthy',
  [MONITORING_HEALTH.NEEDS_ATTENTION]: 'Needs review',
  [MONITORING_HEALTH.CRITICAL]: 'Offline',
};

const STATUS_SORT_ORDER: Record<string, number> = {
  [SENSOR_STATUS.ONLINE]: 0,
  [RECOMMENDATION_HEALTH.CURRENT]: 0,
  [MONITORING_HEALTH.ALL_GOOD]: 0,
  [SENSOR_STATUS.DEGRADED]: 1,
  [RECOMMENDATION_HEALTH.MISSING]: 1,
  [MONITORING_HEALTH.NEEDS_ATTENTION]: 1,
  [SENSOR_STATUS.OFFLINE]: 2,
  [RECOMMENDATION_HEALTH.STALE]: 2,
  [MONITORING_HEALTH.CRITICAL]: 2,
  [RECOMMENDATION_HEALTH.DISABLED]: 3,
};

const compareText = (left?: unknown, right?: unknown) => String(left ?? '').localeCompare(
  String(right ?? ''),
  undefined,
  { numeric: true, sensitivity: 'base' },
);

const compareNumber = (left?: number, right?: number) => (left || 0) - (right || 0);

const compareStatus = (left?: string, right?: string) => (
  (STATUS_SORT_ORDER[left || ''] ?? Number.MAX_SAFE_INTEGER)
  - (STATUS_SORT_ORDER[right || ''] ?? Number.MAX_SAFE_INTEGER)
);

const getSensorAttentionRate = (record: {
  sensors?: number;
  degraded?: number;
  offline?: number;
}) => getPercent((record.degraded || 0) + (record.offline || 0), record.sensors || 0);

const getRecommendationAttentionRate = (record: {
  recommendationCurrent?: number;
  recommendationStale?: number;
  recommendationMissing?: number;
}) => {
  const current = record.recommendationCurrent || 0;
  const attention = (record.recommendationStale || 0) + (record.recommendationMissing || 0);
  return getPercent(attention, current + attention);
};

const formatTimestamp = (value?: string | null) => (
  value ? moment(value).format('YYYY-MM-DD hh:mm A') : '-'
);

const renderStatusTag = (status?: string, displayLabel?: string) => {
  const normalizedStatus = status || SENSOR_STATUS.UNKNOWN;
  return (
    <Tag color={STATUS_TAG_COLORS[normalizedStatus] || 'default'}>
      {displayLabel || STATUS_TAG_LABELS[normalizedStatus] || formatLabel(normalizedStatus)}
    </Tag>
  );
};

const getStatusTooltipContent = (title: string, description: string) => (
  <div className="monitoring-tooltip-content">
    <div className="monitoring-tooltip-content__title">{title}</div>
    <div className="monitoring-tooltip-content__body">{description}</div>
  </div>
);

const MonitoringTooltip: React.FC<{
  title: React.ReactNode;
  children: React.ReactElement;
}> = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="bottom"
    overlayClassName="monitoring-status-tooltip"
    mouseEnterDelay={0.15}
    mouseLeaveDelay={0.1}
  >
    {children}
  </Tooltip>
);

const renderInfoTitle = (title: string, tooltip: React.ReactNode) => (
  <span className="monitoring-table-heading">
    {title}
    <MonitoringTooltip title={tooltip}>
      <InfoCircleOutlined onClick={(event) => event.stopPropagation()} />
    </MonitoringTooltip>
  </span>
);

const renderSensorHealth = (record: {
  sensors?: number;
  online?: number;
  degraded?: number;
  offline?: number;
}, compact = false) => {
  const total = record.sensors || 0;
  const onlineCount = record.online || 0;
  const degradedCount = record.degraded || 0;
  const offlineCount = record.offline || 0;
  const online = getPercent(onlineCount, total);
  const degraded = getPercent(degradedCount, total);
  const offline = getPercent(offlineCount, total);
  return (
    <div className={`monitoring-health-bar ${compact ? 'is-compact' : ''}`}>
      <div className="monitoring-health-bar__track">
        <span className="is-online" style={{ width: `${online}%` }} />
        <span className="is-degraded" style={{ width: `${degraded}%` }} />
        <span className="is-offline" style={{ width: `${offline}%` }} />
      </div>
      <div className="monitoring-health-bar__values">
        <span>
          <i className="is-online" />
          <strong>{onlineCount}</strong>
          {' '}
          {SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE]}
        </span>
        <span>
          <i className="is-degraded" />
          <strong>{degradedCount}</strong>
          {' '}
          {SENSOR_HEALTH_LABELS[SENSOR_STATUS.DEGRADED]}
        </span>
        <span>
          <i className="is-offline" />
          <strong>{offlineCount}</strong>
          {' '}
          {SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE]}
        </span>
      </div>
    </div>
  );
};

const renderRecommendationHealth = (record: {
  recommendationCurrent?: number;
  recommendationStale?: number;
  recommendationMissing?: number;
}) => {
  const current = record.recommendationCurrent || 0;
  const stale = record.recommendationStale || 0;
  const missing = record.recommendationMissing || 0;
  const total = current + stale + missing;
  const currentPercent = getPercent(current, total);
  const stalePercent = getPercent(stale, total);
  const missingPercent = getPercent(missing, total);
  return (
    <div className="monitoring-health-bar is-recommendation">
      <div className="monitoring-health-bar__track">
        <span className="is-online" style={{ width: `${currentPercent}%` }} />
        <span className="is-offline" style={{ width: `${stalePercent}%` }} />
        <span className="is-degraded" style={{ width: `${missingPercent}%` }} />
      </div>
      <div className="monitoring-health-bar__values">
        <span>
          {current}
          {' '}
          recent
        </span>
        <span>
          {stale + missing}
          {' '}
          need review
        </span>
      </div>
    </div>
  );
};

const getMobileDetailItem = (selection: SelectionKey, row: Record<string, any>) => {
  if (selection === SELECTION_KEY.ENTITY) {
    return {
      title: row.name,
      subtitle: `${row.ranches} ranches · ${row.blocks} blocks`,
      status: row.health,
      fields: [
        { label: 'Sensors', value: row.sensors },
        { label: 'Gateways', value: row.gatewayApplicable === false ? 'N/A' : row.gateways },
        { label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE], value: row.online },
        { label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE], value: row.offline },
      ],
    };
  }
  if (selection === SELECTION_KEY.RANCHES) {
    return {
      title: row.name,
      subtitle: row.entityName,
      status: row.health,
      fields: [
        { label: 'Blocks', value: row.blocks },
        { label: 'Sensors', value: row.sensors },
        { label: 'Gateways', value: row.gatewayApplicable === false ? 'N/A' : row.gateways },
        { label: SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE], value: row.offline },
      ],
    };
  }
  if (RECOMMENDATION_SELECTIONS.includes(selection)) {
    return {
      title: row.name,
      subtitle: `${row.ranchName} / ${row.entityName}`,
      status: row.recommendationHealth,
      fields: [{ label: 'Last Recommendation', value: formatTimestamp(row.lastRecommendationTime) }],
    };
  }
  if (GATEWAY_SELECTIONS.includes(selection)) {
    return {
      title: row.name,
      subtitle: row.ranchName,
      status: row.status,
      fields: [
        { label: 'Entity', value: row.entityName },
        { label: 'Connected Sensors', value: row.connectedSensors },
        { label: 'Connected Nodes', value: row.connectedNodes },
      ],
    };
  }
  return {
    title: row.name,
    subtitle: row.sensorType,
    status: row.status,
    fields: [
      { label: 'Ranch', value: row.ranchName },
      { label: 'Gateway', value: row.gatewayName },
    ],
  };
};

const getMobileDetailIcon = (selection: SelectionKey) => {
  if (selection === SELECTION_KEY.ENTITY) return <ApartmentOutlined />;
  if (selection === SELECTION_KEY.RANCHES) return <HomeOutlined />;
  if (RECOMMENDATION_SELECTIONS.includes(selection)) {
    return <AppstoreOutlined />;
  }
  if (GATEWAY_SELECTIONS.includes(selection)) return <ApiOutlined />;
  return <WifiOutlined />;
};

const EntityMonitoring: React.FC = () => {
  const { t } = useTranslation();

  const renderGatewaySummary = (record: MonitoringRanchRow) => {
    const isNotApplicable = record.gatewayApplicable === false;
    return (
      <span className="monitoring-count-cell">
        <strong>{isNotApplicable ? t('N/A') : record.gateways}</strong>
        <small>
          {isNotApplicable ? t('Not required') : `${record.onlineGateways} ${t('online')}`}
        </small>
      </span>
    );
  };

  const compareGatewaySummary = (left: MonitoringRanchRow, right: MonitoringRanchRow) => {
    if (left.gatewayApplicable === false && right.gatewayApplicable !== false) return 1;
    if (left.gatewayApplicable !== false && right.gatewayApplicable === false) return -1;
    return compareNumber(left.gateways, right.gateways);
  };
  const maxWidth = useGetMaxWidth();
  const userInfo = (useSelector(selectLoginUserInfo) as { payload?: { admin?: boolean } })?.payload;
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(DEFAULT_SNAPSHOT_DATE);
  const [selectedRegion, setSelectedRegion] = useState<RegionFilter>(DEFAULT_REGION);
  const [selectedCard, setSelectedCard] = useState<SelectionKey>(DEFAULT_SELECTION);
  const [monitoringRows, setMonitoringRows] = useState<MonitoringRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [columnOrders, setColumnOrders] = useState<ColumnOrderState>(readColumnOrders);
  const [draggedColumnKey, setDraggedColumnKey] = useState<string | null>(null);
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    setIsLoading(true);
    setErrorMessage('');
    getEntityMonitoringData(
      {
        date: selectedDate.format('YYYY-MM-DD'),
        region: selectedRegion === DEFAULT_REGION ? undefined : selectedRegion,
      },
      (response, isSuccess) => {
        if (requestId !== latestRequestIdRef.current) return;
        if (isSuccess) {
          setMonitoringRows(Array.isArray(response) ? response : []);
        } else {
          const nextErrorMessage = t(
            'Unable to load the monitoring dashboard. Please try a different date or refresh later.',
          );
          setMonitoringRows([]);
          setErrorMessage(nextErrorMessage);
          notification.error({
            message: t('Unable to load the monitoring dashboard'),
            description: nextErrorMessage,
          });
        }
        setIsLoading(false);
      },
    );
  }, [selectedDate, selectedRegion, t]);

  if (!userInfo?.admin) return <Page404 />;

  const sensorRows = buildSensorRows(monitoringRows);
  const gatewayRows = buildGatewayRows(monitoringRows);
  const blockRows = buildBlockRows(monitoringRows);
  const ranchRows = buildRanchRows(monitoringRows);
  const entityRows = buildEntityRows(monitoringRows);
  const onlineSensors = sensorRows.filter((row) => row.status === SENSOR_STATUS.ONLINE);
  const degradedSensors = sensorRows.filter((row) => row.status === SENSOR_STATUS.DEGRADED);
  const offlineSensors = sensorRows.filter((row) => row.status === SENSOR_STATUS.OFFLINE);
  const onlineGateways = gatewayRows.filter((row) => row.status === SENSOR_STATUS.ONLINE);
  const offlineGateways = gatewayRows.filter((row) => row.status === SENSOR_STATUS.OFFLINE);
  const currentRecommendations = blockRows.filter(
    (row) => row.recommendationHealth === RECOMMENDATION_HEALTH.CURRENT,
  );
  const staleRecommendations = blockRows.filter(
    (row) => row.recommendationHealth === RECOMMENDATION_HEALTH.STALE,
  );
  const missingRecommendations = blockRows.filter(
    (row) => row.recommendationHealth === RECOMMENDATION_HEALTH.MISSING,
  );
  const totals = monitoringRows.reduce((result, row) => {
    const stats = getDeviceStatsForRow(row);
    return {
      gateways: result.gateways + stats.totalGateways,
      onlineGateways: result.onlineGateways + stats.onlineGateways,
      offlineGateways: result.offlineGateways + stats.offlineGateways,
    };
  }, {
    gateways: 0,
    onlineGateways: 0,
    offlineGateways: 0,
  });
  // The sensor cards and their detail views share one deduplicated sensor collection.
  const sensorTotals = {
    sensors: sensorRows.length,
    onlineSensors: onlineSensors.length,
    degradedSensors: degradedSensors.length,
    offlineSensors: offlineSensors.length,
  };

  const selectCard = (selection: SelectionKey) => {
    setSelectedCard(selection);
    setSearchTerm('');
  };

  const sensorTooltip = getStatusTooltipContent(
    t('How sensor health works'),
    t('Green - Good means the sensor sent at least 80% of its expected readings. Amber - Fair means it sent some readings, but less than 80%. Red - Poor means it sent no readings during this monitoring period.'),
  );
  const recommendationTooltip = getStatusTooltipContent(
    t('How recommendation status works'),
    t('Green - Generated recently means the latest recommendation is no more than 2 days old. Red - Overdue means it is more than 2 days old. Amber - Not generated means no recommendation was found. Gray - Not enabled means recommendations are not configured for the block.'),
  );
  const monitoringStatusTooltip = getStatusTooltipContent(
    t('How overall status works'),
    t('Green - Healthy means no sensor, gateway, or recommendation issues were found. Amber - Needs review means at least one item needs attention. Red - Offline means more than half of the sensors have Poor reporting health.'),
  );
  const gatewayTooltip = getStatusTooltipContent(
    t('How gateway status works'),
    t('Green - Online means the gateway was saved as online in the daily snapshot. Red - Offline means the gateway was saved as offline for the selected date.'),
  );
  const entityTooltip = getStatusTooltipContent(
    t('Monitored entities'),
    t('The number of entities included in the daily monitoring snapshot for the selected date.'),
  );

  const entityColumns: ColumnsType<MonitoringEntityRow> = [
    {
      title: t('Entity'),
      dataIndex: 'name',
      key: 'name',
      width: 230,
      sorter: (left, right) => compareText(left.name, right.name),
      render: (_, record) => (
        <span className="monitoring-name-cell">
          <strong>{record.name}</strong>
          <small>
            {record.ranches}
            {' '}
            {t('ranches')}
            {' '}
            &middot;
            {' '}
            {record.blocks}
            {' '}
            {t('blocks')}
          </small>
        </span>
      ),
    },
    {
      title: t('Sensors'),
      dataIndex: 'sensors',
      key: 'sensors',
      width: 85,
      sorter: (left, right) => compareNumber(left.sensors, right.sensors),
    },
    {
      title: t('Gateways'),
      dataIndex: 'gateways',
      key: 'gateways',
      width: 105,
      sorter: compareGatewaySummary,
      render: (_, record) => renderGatewaySummary(record),
    },
    {
      title: renderInfoTitle(t('Sensor health'), sensorTooltip),
      key: 'sensorHealth',
      width: 235,
      sorter: (left, right) => getSensorAttentionRate(left) - getSensorAttentionRate(right),
      render: (_, record) => renderSensorHealth(record),
    },
    {
      title: renderInfoTitle(t('Recommendation status'), recommendationTooltip),
      key: 'recommendationHealth',
      width: 220,
      sorter: (left, right) => (
        getRecommendationAttentionRate(left) - getRecommendationAttentionRate(right)
      ),
      render: (_, record) => renderRecommendationHealth(record),
    },
    {
      title: renderInfoTitle(t('Overall status'), monitoringStatusTooltip),
      dataIndex: 'health',
      key: 'health',
      width: 125,
      sorter: (left, right) => compareStatus(left.health, right.health),
      render: (health: MonitoringHealth) => renderStatusTag(health),
    },
  ];

  const ranchColumns: ColumnsType<any> = [
    {
      title: t('Ranch'),
      dataIndex: 'name',
      key: 'name',
      width: 190,
      sorter: (left, right) => compareText(left.name, right.name),
    },
    {
      title: t('Entity'),
      dataIndex: 'entityName',
      key: 'entityName',
      width: 170,
      sorter: (left, right) => compareText(left.entityName, right.entityName),
    },
    {
      title: t('Blocks'),
      dataIndex: 'blocks',
      key: 'blocks',
      width: 70,
      sorter: (left, right) => compareNumber(left.blocks, right.blocks),
    },
    {
      title: t('Sensors'),
      dataIndex: 'sensors',
      key: 'sensors',
      width: 80,
      sorter: (left, right) => compareNumber(left.sensors, right.sensors),
    },
    {
      title: t('Gateways'),
      dataIndex: 'gateways',
      key: 'gateways',
      width: 85,
      sorter: compareGatewaySummary,
      render: (_, record) => renderGatewaySummary(record),
    },
    {
      title: renderInfoTitle(t('Sensor health'), sensorTooltip),
      key: 'sensorHealth',
      width: 235,
      sorter: (left, right) => getSensorAttentionRate(left) - getSensorAttentionRate(right),
      render: (_, record) => renderSensorHealth(record),
    },
    {
      title: renderInfoTitle(t('Overall status'), monitoringStatusTooltip),
      dataIndex: 'health',
      key: 'health',
      width: 125,
      sorter: (left, right) => compareStatus(left.health, right.health),
      render: (health: MonitoringHealth) => renderStatusTag(health),
    },
  ];

  const blockColumns: ColumnsType<MonitoringBlockRow> = [
    {
      title: t('Block'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      sorter: (left, right) => compareText(left.name, right.name),
    },
    {
      title: t('Ranch / Entity'),
      key: 'context',
      width: 230,
      sorter: (left, right) => (
        compareText(left.ranchName, right.ranchName)
        || compareText(left.entityName, right.entityName)
      ),
      render: (_, record) => (
        <span className="monitoring-name-cell">
          <strong>{record.ranchName}</strong>
          <small>{record.entityName}</small>
        </span>
      ),
    },
    {
      title: renderInfoTitle(t('Recommendation status'), recommendationTooltip),
      dataIndex: 'recommendationHealth',
      key: 'recommendationHealth',
      width: 190,
      sorter: (left, right) => compareStatus(
        left.recommendationHealth,
        right.recommendationHealth,
      ),
      render: (health) => renderStatusTag(health),
    },
    {
      title: t('Last recommendation'),
      dataIndex: 'lastRecommendationTime',
      key: 'lastRecommendationTime',
      width: 220,
      sorter: (left, right) => compareNumber(
        left.lastRecommendationTime ? moment(left.lastRecommendationTime).valueOf() : 0,
        right.lastRecommendationTime ? moment(right.lastRecommendationTime).valueOf() : 0,
      ),
      render: (value) => formatTimestamp(value),
    },
  ];

  const sensorColumns: ColumnsType<any> = [
    {
      title: t('Sensor Name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (left, right) => compareText(left.name, right.name),
    },
    {
      title: t('Type'),
      dataIndex: 'sensorType',
      key: 'sensorType',
      width: 150,
      sorter: (left, right) => compareText(left.sensorType, right.sensorType),
    },
    {
      title: t('Gateway'),
      dataIndex: 'gatewayName',
      key: 'gatewayName',
      width: 170,
      sorter: (left, right) => compareText(
        getGatewayDisplayValue(left),
        getGatewayDisplayValue(right),
      ),
      render: (_, sensor) => getGatewayDisplayValue(sensor),
    },
    {
      title: t('Ranch'),
      dataIndex: 'ranchName',
      key: 'ranchName',
      width: 170,
      sorter: (left, right) => compareText(left.ranchName, right.ranchName),
    },
    {
      title: t('Entity'),
      dataIndex: 'entityName',
      key: 'entityName',
      width: 170,
      sorter: (left, right) => compareText(left.entityName, right.entityName),
    },
    {
      title: renderInfoTitle(t('Status'), sensorTooltip),
      dataIndex: 'status',
      key: 'status',
      width: 130,
      sorter: (left, right) => compareStatus(left.status, right.status),
      render: (status) => renderStatusTag(
        status,
        SENSOR_HEALTH_LABELS[status as keyof typeof SENSOR_HEALTH_LABELS]
          || SENSOR_HEALTH_LABELS[SENSOR_STATUS.UNKNOWN],
      ),
    },
  ];

  const gatewayColumns: ColumnsType<any> = [
    {
      title: t('Gateway'),
      dataIndex: 'name',
      key: 'name',
      width: 190,
      sorter: (left, right) => compareText(left.name, right.name),
    },
    {
      title: t('Ranch'),
      dataIndex: 'ranchName',
      key: 'ranchName',
      width: 180,
      sorter: (left, right) => compareText(left.ranchName, right.ranchName),
    },
    {
      title: t('Entity'),
      dataIndex: 'entityName',
      key: 'entityName',
      width: 180,
      sorter: (left, right) => compareText(left.entityName, right.entityName),
    },
    {
      title: t('Connected Sensors'),
      dataIndex: 'connectedSensors',
      key: 'connectedSensors',
      width: 145,
      sorter: (left, right) => compareNumber(left.connectedSensors, right.connectedSensors),
    },
    {
      title: t('Connected Nodes'),
      dataIndex: 'connectedNodes',
      key: 'connectedNodes',
      width: 135,
      sorter: (left, right) => compareNumber(left.connectedNodes, right.connectedNodes),
    },
    {
      title: renderInfoTitle(t('Status'), gatewayTooltip),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      sorter: (left, right) => compareStatus(left.status, right.status),
      render: (status) => renderStatusTag(status),
    },
  ];

  const detailConfig: Record<SelectionKey, DetailConfig> = {
    [SELECTION_KEY.ENTITY]: { label: t('Entities'), rows: entityRows, columns: entityColumns },
    [SELECTION_KEY.SENSORS]: { label: t('Sensors'), rows: sensorRows, columns: sensorColumns },
    [SELECTION_KEY.RANCHES]: { label: t('Ranches'), rows: ranchRows, columns: ranchColumns },
    [SELECTION_KEY.BLOCKS]: { label: t('Blocks'), rows: blockRows, columns: blockColumns },
    [SELECTION_KEY.ONLINE]: {
      label: t(`${SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE]} Sensors`),
      rows: onlineSensors,
      columns: sensorColumns,
    },
    [SELECTION_KEY.DEGRADED]: {
      label: t(`${SENSOR_HEALTH_LABELS[SENSOR_STATUS.DEGRADED]} Sensors`),
      rows: degradedSensors,
      columns: sensorColumns,
    },
    [SELECTION_KEY.OFFLINE]: {
      label: t(`${SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE]} Sensors`),
      rows: offlineSensors,
      columns: sensorColumns,
    },
    [SELECTION_KEY.GATEWAYS]: {
      label: t('Gateways'), rows: gatewayRows, columns: gatewayColumns
    },
    [SELECTION_KEY.GATEWAY_ONLINE]: {
      label: t('Online Gateways'), rows: onlineGateways, columns: gatewayColumns
    },
    [SELECTION_KEY.GATEWAY_OFFLINE]: {
      label: t('Offline Gateways'), rows: offlineGateways, columns: gatewayColumns
    },
    [SELECTION_KEY.RECOMMENDATION_CURRENT]: {
      label: t('Recently Generated Recommendations'),
      rows: currentRecommendations,
      columns: blockColumns,
    },
    [SELECTION_KEY.RECOMMENDATION_STALE]: {
      label: t('Overdue Recommendations'),
      rows: staleRecommendations,
      columns: blockColumns,
    },
    [SELECTION_KEY.RECOMMENDATION_MISSING]: {
      label: t('Recommendations Not Generated'),
      rows: missingRecommendations,
      columns: blockColumns,
    },
  };

  let detailFilters: FilterOption[] = [];
  if (SENSOR_SELECTIONS.includes(selectedCard)) {
    detailFilters = [
      { key: SELECTION_KEY.SENSORS, label: t('All'), count: sensorRows.length },
      {
        key: SELECTION_KEY.ONLINE,
        label: t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE]),
        count: onlineSensors.length,
        tone: 'online',
      },
      {
        key: SELECTION_KEY.DEGRADED,
        label: t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.DEGRADED]),
        count: degradedSensors.length,
        tone: 'degraded',
      },
      {
        key: SELECTION_KEY.OFFLINE,
        label: t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE]),
        count: offlineSensors.length,
        tone: 'offline',
      },
    ];
  } else if (GATEWAY_SELECTIONS.includes(selectedCard)) {
    detailFilters = [
      { key: SELECTION_KEY.GATEWAYS, label: t('All'), count: gatewayRows.length },
      {
        key: SELECTION_KEY.GATEWAY_ONLINE,
        label: t('Online'),
        count: onlineGateways.length,
        tone: 'online'
      },
      {
        key: SELECTION_KEY.GATEWAY_OFFLINE,
        label: t('Offline'),
        count: offlineGateways.length,
        tone: 'offline'
      },
    ];
  } else if (RECOMMENDATION_SELECTIONS.includes(selectedCard)) {
    detailFilters = [
      { key: SELECTION_KEY.BLOCKS, label: t('All'), count: blockRows.length },
      {
        key: SELECTION_KEY.RECOMMENDATION_CURRENT,
        label: t('Generated recently'),
        count: currentRecommendations.length,
        tone: 'online'
      },
      {
        key: SELECTION_KEY.RECOMMENDATION_STALE,
        label: t('Overdue'),
        count: staleRecommendations.length,
        tone: 'offline'
      },
      {
        key: SELECTION_KEY.RECOMMENDATION_MISSING,
        label: t('Not generated'),
        count: missingRecommendations.length,
        tone: 'degraded'
      },
    ];
  }

  const activeDetail = detailConfig[selectedCard];
  const activeColumnGroup = COLUMN_GROUP_BY_SELECTION[selectedCard];
  const defaultColumnOrder = activeDetail.columns.map(getColumnKey);
  const activeColumnOrder = normalizeColumnOrder(
    columnOrders[activeColumnGroup],
    defaultColumnOrder,
  );
  const orderedColumns = activeColumnOrder.map((columnKey) => activeDetail.columns.find(
    (column, index) => getColumnKey(column, index) === columnKey,
  )).filter(Boolean) as ColumnsType<any>;
  const filteredDetailRows = activeDetail.rows.filter((row) => (
    !searchTerm || Object.values(row).some((value) => String(value || '')
      .toLowerCase()
      .includes(searchTerm.toLowerCase()))
  ));
  const selectedEntity = selectedEntityId
    ? buildEntityDrawerData(monitoringRows, selectedEntityId)
    : null;

  const openEntityDrawer = (record: MonitoringEntityRow) => setSelectedEntityId(String(record.id));
  const saveColumnOrders = (nextOrders: ColumnOrderState) => {
    setColumnOrders(nextOrders);
    try {
      window.localStorage.setItem(COLUMN_ORDER_STORAGE_KEY, JSON.stringify(nextOrders));
    } catch (error) {
      // The selected order still works for this session when browser storage is unavailable.
    }
  };
  const reorderActiveColumns = (sourceKey: string, targetKey: string) => {
    const nextOrder = moveColumn(activeColumnOrder, sourceKey, targetKey);
    if (nextOrder === activeColumnOrder) return;
    saveColumnOrders({ ...columnOrders, [activeColumnGroup]: nextOrder });
  };
  const resetActiveColumnOrder = () => {
    const nextOrders = { ...columnOrders };
    delete nextOrders[activeColumnGroup];
    saveColumnOrders(nextOrders);
  };
  const columnManager = (
    <div className="monitoring-column-manager">
      <Typography.Text className="monitoring-column-manager__help">
        {t('Drag columns into the order you want.')}
      </Typography.Text>
      <div className="monitoring-column-manager__list">
        {activeColumnOrder.map((columnKey, index) => {
          const column = activeDetail.columns.find(
            (candidate, candidateIndex) => getColumnKey(candidate, candidateIndex) === columnKey,
          );
          const title = COLUMN_LABELS[columnKey]
            || (typeof column?.title === 'string' ? column.title : formatLabel(columnKey));
          return (
            <button
              type="button"
              draggable
              key={columnKey}
              className={`monitoring-column-manager__item ${draggedColumnKey === columnKey ? 'is-dragging' : ''}`}
              onDragStart={() => setDraggedColumnKey(columnKey)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                if (draggedColumnKey) reorderActiveColumns(draggedColumnKey, columnKey);
                setDraggedColumnKey(null);
              }}
              onDragEnd={() => setDraggedColumnKey(null)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowUp' && index > 0) {
                  event.preventDefault();
                  reorderActiveColumns(columnKey, activeColumnOrder[index - 1]);
                }
                if (event.key === 'ArrowDown' && index < activeColumnOrder.length - 1) {
                  event.preventDefault();
                  reorderActiveColumns(columnKey, activeColumnOrder[index + 1]);
                }
              }}
            >
              <MenuOutlined />
              <span>{t(title)}</span>
            </button>
          );
        })}
      </div>
      <Button
        type="text"
        size="small"
        icon={<ReloadOutlined />}
        onClick={resetActiveColumnOrder}
        className="monitoring-column-manager__reset"
      >
        {t('Reset to default')}
      </Button>
    </div>
  );
  const rowClassName = (record: Record<string, any>, index: number) => {
    if ((selectedCard === SELECTION_KEY.ENTITY || selectedCard === SELECTION_KEY.RANCHES)
      && record.health === MONITORING_HEALTH.CRITICAL) {
      return 'monitoring-table-row--critical';
    }
    if ((selectedCard === SELECTION_KEY.BLOCKS
      || selectedCard === SELECTION_KEY.RECOMMENDATION_STALE)
      && record.recommendationHealth === RECOMMENDATION_HEALTH.STALE) {
      return 'monitoring-table-row--stale';
    }
    if ((selectedCard === SELECTION_KEY.BLOCKS
      || selectedCard === SELECTION_KEY.RECOMMENDATION_MISSING)
      && record.recommendationHealth === RECOMMENDATION_HEALTH.MISSING) {
      return 'monitoring-table-row--attention';
    }
    return index % 2 === 0 ? '' : 'table-row-dark';
  };

  const detailContent = isMobile ? (
    <div className="monitoring-dashboard__mobile-list">
      {filteredDetailRows.map((row) => {
        const item = getMobileDetailItem(selectedCard, row);
        const isEntity = selectedCard === SELECTION_KEY.ENTITY;
        const isSensor = SENSOR_SELECTIONS.includes(selectedCard);
        const sensorStatusLabel = isSensor
          ? SENSOR_HEALTH_LABELS[item.status as keyof typeof SENSOR_HEALTH_LABELS]
            || SENSOR_HEALTH_LABELS[SENSOR_STATUS.UNKNOWN]
          : undefined;
        return (
          <button
            type="button"
            key={row.id}
            className={`monitoring-mobile-card ${row.health === MONITORING_HEALTH.CRITICAL ? 'is-critical' : ''}`}
            onClick={() => { if (isEntity) openEntityDrawer(row as MonitoringEntityRow); }}
          >
            <div className="monitoring-mobile-card__header">
              <div className="monitoring-mobile-card__lead">
                <span className="monitoring-mobile-card__icon">
                  {getMobileDetailIcon(selectedCard)}
                </span>
                <span className="monitoring-mobile-card__title-group">
                  <Typography.Text className="monitoring-mobile-card__title">{item.title}</Typography.Text>
                  <Typography.Text className="monitoring-mobile-card__subtitle">{item.subtitle}</Typography.Text>
                </span>
              </div>
              <span className="monitoring-mobile-card__trailing">
                {renderStatusTag(item.status, sensorStatusLabel)}
              </span>
            </div>
            <div className="monitoring-mobile-card__fields">
              {item.fields.map((field) => (
                <span key={`${row.id}-${field.label}`} className="monitoring-mobile-card__field">
                  <Typography.Text className="monitoring-mobile-card__field-label">{t(field.label)}</Typography.Text>
                  <strong className="monitoring-mobile-card__field-value">{field.value}</strong>
                </span>
              ))}
            </div>
          </button>
        );
      })}
    </div>
  ) : (
    <EditableTable
      key={selectedCard}
      data={filteredDetailRows}
      columns={orderedColumns}
      showIsActive={false}
      showAction={false}
      hidePagination={false}
      isDataSorted
      pageSize={8}
      showSizeChanger
      pageSizeOptions={['8', '16', '24', '40']}
      showTotal
      rowClassName={rowClassName}
      onRow={selectedCard === SELECTION_KEY.ENTITY ? (record) => ({
        onClick: () => openEntityDrawer(record),
        onKeyDown: (event) => {
          if (event.key === 'Enter' || event.key === ' ') openEntityDrawer(record);
        },
        tabIndex: 0,
        role: 'button',
      }) : undefined}
      scroll={{ x: 1000 }}
    />
  );

  return (
    <div
      className="single-column-page padded desktop-container desktop-container-config"
      style={!isMobile ? { maxWidth } : {}}
    >
      <div className="monitoring-dashboard">
        <div className="monitoring-dashboard__header">
          <div>
            <Typography.Title level={3} className="monitoring-dashboard__title">
              {t('Entity Monitoring')}
            </Typography.Title>
            <Typography.Text className="monitoring-dashboard__subtitle">
              {t('Daily operational health across monitored organizations.')}
            </Typography.Text>
          </div>
          <div className="monitoring-dashboard__filter-controls">
            <div className="monitoring-dashboard__region-filter">
              <Typography.Text className="monitoring-dashboard__filter-label">
                {t('Region')}
              </Typography.Text>
              <Select
                aria-label={t('Region')}
                className="monitoring-dashboard__region-select"
                disabled={isLoading}
                value={selectedRegion}
                onChange={(value: RegionFilter) => {
                  setSelectedRegion(value);
                  setSelectedEntityId(null);
                }}
              >
                {REGION_OPTIONS.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {t(option.label)}
                  </Select.Option>
                ))}
              </Select>
            </div>
            <div className="monitoring-dashboard__date-filter">
              <Typography.Text className="monitoring-dashboard__filter-label">
                {t('Monitoring date')}
              </Typography.Text>
              <DatePicker
                allowClear={false}
                className="monitoring-dashboard__date-picker"
                disabled={isLoading}
                disabledDate={(date) => Boolean(date && date.isAfter(moment(), 'day'))}
                value={selectedDate}
                onChange={(value) => { if (value) setSelectedDate(value); }}
              />
            </div>
          </div>
        </div>

        <div className={`monitoring-dashboard__content ${isLoading ? 'is-loading' : ''}`}>
          {isLoading ? (
            <div className="monitoring-dashboard__loading-overlay">
              <Spin indicator={LOADING_INDICATOR} />
              <Typography.Text className="monitoring-dashboard__loading-text">
                {t('Loading monitoring data for the selected date...')}
              </Typography.Text>
            </div>
          ) : null}

          {errorMessage ? (
            <Alert className="monitoring-dashboard__error" message={errorMessage} type="error" showIcon />
          ) : null}

          <div className="monitoring-dashboard__operations-grid">
            <button
              type="button"
              className={`monitoring-entity-card ${selectedCard === SELECTION_KEY.ENTITY ? 'is-active' : ''}`}
              onClick={() => selectCard(SELECTION_KEY.ENTITY)}
            >
              <span className="monitoring-entity-card__title">
                {t('Entities')}
                <MonitoringTooltip title={entityTooltip}><InfoCircleOutlined /></MonitoringTooltip>
              </span>
              <span className="monitoring-entity-card__metric">
                <ApartmentOutlined />
                <strong>{entityRows.length}</strong>
              </span>
            </button>

            <section className={`monitoring-health-card ${SENSOR_SELECTIONS.includes(selectedCard) ? 'is-active' : ''}`}>
              <button
                type="button"
                className="monitoring-health-card__header"
                onClick={() => selectCard(SELECTION_KEY.SENSORS)}
              >
                <span>
                  <strong>{t('Sensor health')}</strong>
                  <MonitoringTooltip title={sensorTooltip}>
                    <InfoCircleOutlined />
                  </MonitoringTooltip>
                </span>
                <b>{sensorTotals.sensors}</b>
              </button>
              <div className="monitoring-health-card__bar">
                <span className="is-online" style={{ width: `${getPercent(sensorTotals.onlineSensors, sensorTotals.sensors)}%` }} />
                <span className="is-degraded" style={{ width: `${getPercent(sensorTotals.degradedSensors, sensorTotals.sensors)}%` }} />
                <span className="is-offline" style={{ width: `${getPercent(sensorTotals.offlineSensors, sensorTotals.sensors)}%` }} />
              </div>
              <div className="monitoring-health-card__segments">
                <button type="button" onClick={() => selectCard(SELECTION_KEY.ONLINE)}>
                  <span className="is-online" />
                  {' '}
                  {t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.ONLINE])}
                  {' '}
                  <strong>{sensorTotals.onlineSensors}</strong>
                </button>
                <button type="button" onClick={() => selectCard(SELECTION_KEY.DEGRADED)}>
                  <span className="is-degraded" />
                  {' '}
                  {t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.DEGRADED])}
                  {' '}
                  <strong>{sensorTotals.degradedSensors}</strong>
                </button>
                <button type="button" onClick={() => selectCard(SELECTION_KEY.OFFLINE)}>
                  <span className="is-offline" />
                  {' '}
                  {t(SENSOR_HEALTH_LABELS[SENSOR_STATUS.OFFLINE])}
                  {' '}
                  <strong>{sensorTotals.offlineSensors}</strong>
                </button>
              </div>
            </section>

            <button
              type="button"
              className={`monitoring-operation-card is-online ${selectedCard === SELECTION_KEY.GATEWAY_ONLINE ? 'is-active' : ''}`}
              onClick={() => selectCard(SELECTION_KEY.GATEWAY_ONLINE)}
            >
              <span className="monitoring-operation-card__title">
                {t('Gateways online')}
                <MonitoringTooltip title={gatewayTooltip}><InfoCircleOutlined /></MonitoringTooltip>
              </span>
              <span className="monitoring-operation-card__metric">
                <ApiOutlined />
                <strong>{totals.onlineGateways}</strong>
                <span className="monitoring-operation-card__total">
                  /
                  {' '}
                  {totals.gateways}
                </span>
              </span>
            </button>

            <button
              type="button"
              className={`monitoring-operation-card is-offline ${selectedCard === SELECTION_KEY.GATEWAY_OFFLINE ? 'is-active' : ''}`}
              onClick={() => selectCard(SELECTION_KEY.GATEWAY_OFFLINE)}
            >
              <span className="monitoring-operation-card__title">
                {t('Gateways offline')}
                <MonitoringTooltip title={gatewayTooltip}><InfoCircleOutlined /></MonitoringTooltip>
              </span>
              <span className="monitoring-operation-card__metric">
                <ApiOutlined />
                <strong>{totals.offlineGateways}</strong>
                <span className="monitoring-operation-card__total">
                  /
                  {' '}
                  {totals.gateways}
                </span>
              </span>
            </button>

            <section className={`monitoring-recommendation-card ${RECOMMENDATION_SELECTIONS.includes(selectedCard) && selectedCard !== SELECTION_KEY.BLOCKS ? 'is-active' : ''}`}>
              <div className="monitoring-recommendation-card__title">
                <strong>{t('Recommendation status')}</strong>
                <MonitoringTooltip title={recommendationTooltip}>
                  <InfoCircleOutlined />
                </MonitoringTooltip>
              </div>
              <div className="monitoring-recommendation-card__segments">
                <button
                  type="button"
                  onClick={() => selectCard(SELECTION_KEY.RECOMMENDATION_CURRENT)}
                >
                  <span className="is-current" />
                  {' '}
                  {t('Generated recently')}
                  <strong>{currentRecommendations.length}</strong>
                </button>
                <button
                  type="button"
                  onClick={() => selectCard(SELECTION_KEY.RECOMMENDATION_STALE)}
                >
                  <span className="is-stale" />
                  {' '}
                  {t('Overdue')}
                  <strong>{staleRecommendations.length}</strong>
                </button>
                <button
                  type="button"
                  onClick={() => selectCard(SELECTION_KEY.RECOMMENDATION_MISSING)}
                >
                  <span className="is-missing" />
                  {' '}
                  {t('Not generated')}
                  <strong>{missingRecommendations.length}</strong>
                </button>
              </div>
            </section>
          </div>

          <div className="monitoring-dashboard__details-panel">
            <div className="monitoring-dashboard__details-header">
              <div className="monitoring-dashboard__details-heading">
                <Typography.Text className="monitoring-dashboard__details-section">
                  {activeDetail.label}
                  {' '}
                  (
                  {activeDetail.rows.length}
                  )
                </Typography.Text>
                {detailFilters.length ? (
                  <div className="monitoring-dashboard__filters">
                    {detailFilters.map((filter) => (
                      <button
                        type="button"
                        key={filter.key}
                        className={`is-${filter.tone || 'default'} ${selectedCard === filter.key ? 'is-active' : ''}`}
                        onClick={() => selectCard(filter.key)}
                      >
                        {filter.label}
                        {' '}
                        {filter.count}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="monitoring-dashboard__details-actions">
                {!isMobile ? (
                  <Popover
                    title={t('Arrange columns')}
                    content={columnManager}
                    trigger="click"
                    placement="bottomRight"
                    overlayClassName="monitoring-column-popover"
                  >
                    <Button
                      icon={<SettingOutlined />}
                      className="monitoring-dashboard__columns-button"
                    >
                      {t('Columns')}
                    </Button>
                  </Popover>
                ) : null}
                <Input.Search
                  allowClear
                  placeholder={t(`Search ${activeDetail.label.toLowerCase()}...`)}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="monitoring-dashboard__search"
                />
              </div>
            </div>

            {filteredDetailRows.length ? detailContent : (
              <div className="monitoring-dashboard__empty-state">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={searchTerm
                    ? t('No matching records found.')
                    : t('No records are available for this selection and date.')}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <EntityMonitoringDrawer
        visible={Boolean(selectedEntityId)}
        data={selectedEntity}
        snapshotDate={selectedDate.format('YYYY-MM-DD')}
        onClose={() => setSelectedEntityId(null)}
      />
    </div>
  );
};

export default EntityMonitoring;
